/* eslint-disable jsdoc/require-jsdoc */
import { Button, Drawer, Input, Message, Select } from "@arco-design/web-react";
import {
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

import { isHttpClientError } from "../../../lib/http/errors";
import {
  getCityOptions,
  getDistrictOptions,
  getProvinceOptions,
} from "../../../shared/region";
import { supplierSubjectNatureOptions, type SupplierKindMeta } from "../config";
import {
  emptySupplierMutation,
  mapSupplierMutationError,
  mutationFromItem,
  validateSupplierMutation,
  type SupplierMutationFieldErrors,
  type SupplierMutationFieldKey,
} from "../service/supplierBasicService";
import type {
  GroundOption,
  SupplierItem,
  SupplierKind,
  SupplierMutation,
  UserOption,
} from "../types";
import "./SupplyBasicPage.css";

type ModalState = { type: "create" } | { type: "edit"; item: SupplierItem };

type SupplyBasicMutationModalProps = {
  kind: SupplierKind;
  meta: SupplierKindMeta;
  state: ModalState | null;
  onClose: () => void;
  errorMessage: string;
  groundOptions: GroundOption[];
  userOptions: UserOption[];
  onSearchUserOptions: (query: string) => void;
  onSearchGroundOptions: (query: string) => void;
  onCreate: (payload: SupplierMutation) => Promise<void>;
  onUpdate: (item: SupplierItem, payload: SupplierMutation) => Promise<void>;
  onClearError: () => void;
};

type FormRowProps = {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
};

function FormRow({ label, required, error, children }: FormRowProps) {
  return (
    <div className="supply-basic-form-row">
      <div className="supply-basic-form-label">
        {required ? <span className="supply-basic-form-required">*</span> : null}
        {label}
      </div>
      <div className="supply-basic-form-control">
        {children}
        {error ? <div className="supply-basic-field-error">{error}</div> : null}
      </div>
    </div>
  );
}

function withCurrentValue(options: string[], current: string) {
  return current && !options.includes(current) ? [current, ...options] : options;
}

function filterOption(
  inputValue: string,
  option: ReactElement,
) {
  const keyword = inputValue.trim().toLowerCase();
  const optionProps = option.props as { value?: unknown; children?: unknown };
  const candidates = [optionProps.value, optionProps.children]
    .filter((value): value is string | number =>
      typeof value === "string" || typeof value === "number",
    )
    .map((value) => String(value).toLowerCase());
  return !keyword || candidates.some((value) => value.includes(keyword));
}

/** Supplier-subject create/edit drawer. Child records have independent lifecycles. */
export function SupplyBasicMutationModal({
  kind,
  meta,
  state,
  onClose,
  errorMessage,
  groundOptions,
  userOptions,
  onSearchUserOptions,
  onSearchGroundOptions,
  onCreate,
  onUpdate,
  onClearError,
}: SupplyBasicMutationModalProps) {
  const [payload, setPayload] = useState<SupplierMutation>(emptySupplierMutation());
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<SupplierMutationFieldErrors>({});

  useEffect(() => {
    if (!state) return;
    onClearError();
    setFieldErrors({});
    setPayload(state.type === "edit" ? mutationFromItem(state.item) : emptySupplierMutation());
  }, [state]);

  const provinceOptions = useMemo(
    () =>
      withCurrentValue(getProvinceOptions(), payload.province).map((value) => ({
        label: value,
        value,
      })),
    [payload.province],
  );
  const cityOptions = useMemo(
    () =>
      withCurrentValue(getCityOptions(payload.province), payload.city).map((value) => ({
        label: value,
        value,
      })),
    [payload.city, payload.province],
  );
  const districtOptions = useMemo(
    () =>
      withCurrentValue(
        getDistrictOptions(payload.province, payload.city),
        payload.district,
      ).map((value) => ({ label: value, value })),
    [payload.city, payload.district, payload.province],
  );
  const userSelectOptions = useMemo(() => {
    const options = userOptions.map((option) => ({
      label: option.phone ? `${option.displayName} (${option.phone})` : option.displayName,
      value: option.userId,
    }));
    if (
      payload.guide.boundUserId &&
      !options.some((option) => option.value === payload.guide.boundUserId)
    ) {
      options.unshift({
        label: payload.guide.boundUserName || String(payload.guide.boundUserId),
        value: payload.guide.boundUserId,
      });
    }
    return options;
  }, [payload.guide.boundUserId, payload.guide.boundUserName, userOptions]);
  const groundSelectOptions = useMemo(() => {
    const options = groundOptions.map((option) => ({
      label: option.supplierName,
      value: option.supplierId,
    }));
    if (
      payload.parentSupplierId &&
      !options.some((option) => option.value === payload.parentSupplierId)
    ) {
      options.unshift({
        label: payload.parentSupplierName || payload.parentSupplierId,
        value: payload.parentSupplierId,
      });
    }
    return options;
  }, [groundOptions, payload.parentSupplierId, payload.parentSupplierName]);

  if (!state) return null;

  const patch = (partial: Partial<SupplierMutation>) =>
    setPayload((current) => ({ ...current, ...partial }));
  const clearFieldError = (field: SupplierMutationFieldKey) =>
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });

  const handleSubmit = async () => {
    const errors = validateSupplierMutation(kind, payload);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      Message.warning(Object.values(errors).find(Boolean) || "请完善必填项");
      return;
    }
    setSubmitting(true);
    try {
      if (state.type === "create") await onCreate(payload);
      else await onUpdate(state.item, payload);
      Message.success("保存成功");
      onClose();
    } catch (error) {
      Message.error(
        mapSupplierMutationError(
          isHttpClientError(error) ? error.code : null,
          "保存失败，请检查填写内容后重试。",
          isHttpClientError(error)
            ? error.message
            : error instanceof Error
              ? error.message
              : undefined,
          isHttpClientError(error) ? error.statusCode : null,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const subjectFields = (
    <>
      <FormRow label={nameLabel(kind)} required error={fieldErrors.supplierName}>
        <Input
          value={payload.supplierName}
          onChange={(supplierName) => {
            clearFieldError("supplierName");
            patch({ supplierName });
          }}
        />
      </FormRow>
      <FormRow label="简称">
        <Input
          value={payload.supplierShortName}
          onChange={(supplierShortName) => patch({ supplierShortName })}
        />
      </FormRow>
      <FormRow label="省份">
        <Select
          allowClear
          showSearch
          filterOption={filterOption}
          value={payload.province || undefined}
          options={provinceOptions}
          onChange={(province) =>
            patch({ province: province || "", city: "", district: "" })
          }
        />
      </FormRow>
      <FormRow label="城市">
        <Select
          allowClear
          showSearch
          filterOption={filterOption}
          value={payload.city || undefined}
          options={cityOptions}
          onChange={(city) => patch({ city: city || "", district: "" })}
        />
      </FormRow>
      <FormRow label="区县">
        <Select
          allowClear
          showSearch
          filterOption={filterOption}
          value={payload.district || undefined}
          options={districtOptions}
          onChange={(district) => patch({ district: district || "" })}
        />
      </FormRow>
      <FormRow label="主体登记地址">
        <Input
          value={payload.addressDetail}
          onChange={(addressDetail) => patch({ addressDetail })}
        />
      </FormRow>
      {meta.showParentGround ? (
        <FormRow label="所属地接">
          <Select
            allowClear
            showSearch
            filterOption={false}
            value={payload.parentSupplierId || undefined}
            options={groundSelectOptions}
            onSearch={onSearchGroundOptions}
            onChange={(parentSupplierId) =>
              patch({
                parentSupplierId: parentSupplierId || "",
                parentSupplierName:
                  groundSelectOptions.find((option) => option.value === parentSupplierId)
                    ?.label || "",
              })
            }
          />
        </FormRow>
      ) : null}
      <FormRow label="经营主体名称">
        <Input
          value={payload.companySubjectName}
          onChange={(companySubjectName) => patch({ companySubjectName })}
        />
      </FormRow>
      <FormRow label="统一社会信用代码">
        <Input value={payload.creditCode} onChange={(creditCode) => patch({ creditCode })} />
      </FormRow>
      <FormRow label="税号">
        <Input value={payload.taxNo} onChange={(taxNo) => patch({ taxNo })} />
      </FormRow>
      <FormRow label="经营范围">
        <Input.TextArea
          value={payload.businessScopeDesc}
          onChange={(businessScopeDesc) => patch({ businessScopeDesc })}
        />
      </FormRow>
      <FormRow label="主体用途">
        <div className="supply-basic-checkbox-grid">
          <label>
            <input
              type="checkbox"
              checked={Boolean(payload.canBeSettlementSubject)}
              onChange={(event) => patch({ canBeSettlementSubject: event.target.checked })}
            />
            结算主体
          </label>
          <label>
            <input
              type="checkbox"
              checked={Boolean(payload.canBeFulfillmentSubject)}
              onChange={(event) => patch({ canBeFulfillmentSubject: event.target.checked })}
            />
            履约主体
          </label>
          <label>
            <input
              type="checkbox"
              checked={Boolean(payload.canBeQuoteSubject)}
              onChange={(event) => patch({ canBeQuoteSubject: event.target.checked })}
            />
            报价主体
          </label>
        </div>
      </FormRow>
    </>
  );

  return (
    <Drawer
      className="supply-basic-mutation-drawer"
      title={state.type === "create" ? meta.createLabel : `修改 ${state.item.supplierName}`}
      visible
      placement="right"
      width={552}
      unmountOnExit
      focusLock={false}
      onCancel={onClose}
      footer={
        <div className="supply-basic-drawer-footer">
          {errorMessage ? (
            <div className="supply-basic-drawer-footer-error" role="alert">
              {errorMessage}
            </div>
          ) : null}
          <Button type="primary" long loading={submitting} onClick={() => void handleSubmit()}>
            保存
          </Button>
        </div>
      }
    >
      <div className="supply-basic-drawer-form">
        {subjectFields}
        {kind === "guides" ? (
          <GuideFields
            payload={payload}
            fieldErrors={fieldErrors}
            userOptions={userSelectOptions}
            onSearch={onSearchUserOptions}
            onPatch={patch}
            onClearError={clearFieldError}
          />
        ) : null}
        {kind === "buses" ? <BusFields payload={payload} onPatch={patch} /> : null}
        {kind === "grounds" ? <GroundFields payload={payload} onPatch={patch} /> : null}
        {kind === "hotels" && state.type === "edit" && state.item.hotel ? (
          <LegacyHotelArchive item={state.item} />
        ) : null}
        <FormRow label="主体备注">
          <Input.TextArea value={payload.remark} onChange={(remark) => patch({ remark })} />
        </FormRow>
      </div>
    </Drawer>
  );
}

export function GuideFields({
  payload,
  fieldErrors,
  userOptions,
  onSearch,
  onPatch,
  onClearError,
}: {
  payload: SupplierMutation;
  fieldErrors: SupplierMutationFieldErrors;
  userOptions: Array<{ label: string; value: number }>;
  onSearch: (query: string) => void;
  onPatch: (partial: Partial<SupplierMutation>) => void;
  onClearError: (field: SupplierMutationFieldKey) => void;
}) {
  return (
    <>
      <FormRow label="主体性质" required>
        <Select
          value={payload.subjectNature}
          options={supplierSubjectNatureOptions.slice(1)}
          onChange={(subjectNature) => {
            const individual = subjectNature === "individual";
            if (!individual) {
              onClearError("boundUserId");
              onClearError("guideCredentialNo");
            }
            onPatch({
              subjectNature,
              guide: individual
                ? payload.guide
                : {
                    ...payload.guide,
                    boundUserId: undefined,
                    boundUserName: "",
                    guideCredentialNo: "",
                  },
            });
          }}
        />
      </FormRow>
      {payload.subjectNature === "individual" ? (
        <>
          <FormRow label="花儿用户" required error={fieldErrors.boundUserId}>
            <Select
              allowClear
              showSearch
              filterOption={false}
              value={payload.guide.boundUserId}
              options={userOptions}
              onSearch={onSearch}
              onChange={(boundUserId) => {
                onClearError("boundUserId");
                onPatch({
                  guide: {
                    ...payload.guide,
                    boundUserId: boundUserId || undefined,
                    boundUserName:
                      userOptions.find((option) => option.value === boundUserId)?.label || "",
                  },
                });
              }}
            />
          </FormRow>
          <FormRow label="导游证编码" required error={fieldErrors.guideCredentialNo}>
            <Input
              value={payload.guide.guideCredentialNo}
              onChange={(guideCredentialNo) => {
                onClearError("guideCredentialNo");
                onPatch({ guide: { ...payload.guide, guideCredentialNo } });
              }}
            />
          </FormRow>
        </>
      ) : null}
      <FormRow label="最高学历">
        <Input
          value={payload.guide.academic}
          onChange={(academic) => onPatch({ guide: { ...payload.guide, academic } })}
        />
      </FormRow>
      <FormRow label="导游简历">
        <Input.TextArea
          value={payload.guide.guideResume}
          onChange={(guideResume) => onPatch({ guide: { ...payload.guide, guideResume } })}
        />
      </FormRow>
      <FormRow label="带队经验">
        <Input.TextArea
          value={payload.guide.teamExperience}
          onChange={(teamExperience) =>
            onPatch({ guide: { ...payload.guide, teamExperience } })
          }
        />
      </FormRow>
    </>
  );
}

function BusFields({
  payload,
  onPatch,
}: {
  payload: SupplierMutation;
  onPatch: (partial: Partial<SupplierMutation>) => void;
}) {
  const patchBus = (partial: Partial<SupplierMutation["bus"]>) =>
    onPatch({ bus: { ...payload.bus, ...partial } });
  return (
    <>
      <FormRow label="车队规模">
        <Input value={payload.bus.scaleText} onChange={(scaleText) => patchBus({ scaleText })} />
      </FormRow>
      <FormRow label="调度地址">
        <Input
          value={payload.bus.navigationAddress}
          onChange={(navigationAddress) => patchBus({ navigationAddress })}
        />
      </FormRow>
      <FormRow label="传真">
        <Input value={payload.bus.fax} onChange={(fax) => patchBus({ fax })} />
      </FormRow>
      <FormRow label="协议名称">
        <Input
          value={payload.bus.protocolName}
          onChange={(protocolName) => patchBus({ protocolName })}
        />
      </FormRow>
    </>
  );
}

function GroundFields({
  payload,
  onPatch,
}: {
  payload: SupplierMutation;
  onPatch: (partial: Partial<SupplierMutation>) => void;
}) {
  const patchGround = (partial: Partial<SupplierMutation["ground"]>) =>
    onPatch({ ground: { ...payload.ground, ...partial } });
  return (
    <>
      <FormRow label="地接介绍">
        <Input.TextArea
          value={payload.ground.describeText}
          onChange={(describeText) => patchGround({ describeText })}
        />
      </FormRow>
      <FormRow label="人员规模">
        <Input
          value={payload.ground.scaleText}
          onChange={(scaleText) => patchGround({ scaleText })}
        />
      </FormRow>
    </>
  );
}

function LegacyHotelArchive({ item }: { item: SupplierItem }) {
  const hotel = item.hotel;
  if (!hotel) return null;
  const rows = [
    ["酒店档次", hotel.housLevelName],
    ["历史导航地址", hotel.navigationAddress],
    ["历史介绍", hotel.introduceText],
    ["历史参考价", hotel.priceRefText],
    ["历史接待量", hotel.receiveCountText],
    ["最近装修日期", hotel.lastDecorateDate],
    ["餐饮信息", hotel.foodInfoText],
    ["周边信息", hotel.nearbyText],
    ["会议室信息", hotel.meetRoomInfoText],
    ["传真", hotel.fax],
    ["出团说明会备注", hotel.routeBriefingRemark],
    ["酒店备注", hotel.hotelRemark],
  ].filter(([, value]) => Boolean(value));
  return (
    <section className="supply-basic-legacy-archive" aria-label="历史酒店档案">
      <h3>历史酒店档案</h3>
      {rows.length ? (
        <dl>
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="supply-basic-empty">无历史酒店档案</div>
      )}
    </section>
  );
}

function nameLabel(kind: SupplierKind) {
  if (kind === "guides") return "导游姓名";
  if (kind === "buses") return "车队名称";
  if (kind === "hotels") return "酒店供应商主体名称";
  if (kind === "restaurants") return "餐厅供应商名称";
  return "地接名称";
}
